import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import './Services.css';

const CATEGORIES = [
  {
    id: 'build',
    label: 'Build',
    services: [
      {
        id: 'fullstack',
        tag: '01 / build',
        title: 'Full-Stack Development',
        description:
          'Custom web applications engineered for performance, scalability, and seamless user experiences — from database to interface.',
        features: [
          'MERN Stack & Next.js experts',
          'Robust RESTful & GraphQL APIs',
          'Clean, maintainable architecture',
          'End-to-end product delivery',
        ],
      },
      {
        id: 'webapp',
        tag: '02 / build',
        title: 'Web & App Development',
        description:
          'We design and build fast, scalable web and mobile applications that help businesses grow, streamline operations, and deliver exceptional user experiences.',
        features: [
          'Responsive, accessible front-ends',
          'Scalable back-end services',
          'Progressive web apps',
          'Performance-first engineering',
        ],
      },
      {
        id: 'mobile',
        tag: '03 / build',
        title: 'Mobile Applications',
        description:
          'Modern mobile solutions for iOS and Android — cross-platform or native, built to feel fast and native on every device.',
        features: [
          'Cross-platform React Native apps',
          'Native iOS & Android builds',
          'Offline-first data sync',
          'App Store & Play Store launch',
        ],
      },
      {
        id: 'custom-software',
        tag: '04 / build',
        title: 'Custom Software',
        description:
          'Business-focused solutions tailored to your workflows — internal tools, platforms, and systems built around how your team actually operates.',
        features: [
          'Bespoke internal tooling',
          'Workflow automation platforms',
          'Third-party system integrations',
          'Long-term maintainability',
        ],
      },
    ],
  },
  {
    id: 'cloud',
    label: 'Cloud & Ops',
    services: [
      {
        id: 'devops',
        tag: '01 / cloud',
        title: 'DevOps & CI/CD',
        description:
          'Accelerate delivery and efficiency with automated pipelines, continuous integration, and seamless deployment workflows.',
        features: [
          'CI/CD pipeline automation',
          'Docker & Kubernetes containerization',
          'Infrastructure as code',
          'Release & rollback strategy',
        ],
      },
      {
        id: 'cloud-support',
        tag: '02 / cloud',
        title: 'Cloud Deployment & Support',
        description:
          'Scalable cloud infrastructure and ongoing support to keep your applications running smoothly and securely.',
        features: [
          'AWS, GCP & Vercel managed hosting',
          'Serverless microservice architecture',
          '24/7 monitoring & alerting',
          'Cost & performance optimization',
        ],
      },
      {
        id: 'security',
        tag: '03 / cloud',
        title: 'Cybersecurity Lab',
        description:
          'Protect your business with enterprise-grade security solutions, compliance audits, and 24/7 threat monitoring.',
        features: [
          'Penetration testing & audits',
          'Compliance & risk assessments',
          'Threat monitoring & response',
          'Secure architecture review',
        ],
      },
      {
        id: 'qa',
        tag: '04 / cloud',
        title: 'QA Testing & Automations',
        description:
          'Empower your team with expert-led training in AI, Python, and Cloud technologies, equipping them with the skills needed to succeed in the digital age.',
        features: [
          'Automated test suites',
          'Manual & exploratory QA',
          'CI-integrated regression testing',
          'Team training & upskilling',
        ],
      },
    ],
  },
  {
    id: 'intelligence',
    label: 'Intelligence & Strategy',
    services: [
      {
        id: 'ai',
        tag: '01 / intelligence',
        title: 'AI Integrations & Automation',
        description:
          'Seamlessly integrate AI capabilities into your existing applications to enhance functionality, improve efficiency, and drive innovation across your workflows.',
        features: [
          'LLM & chat assistant integration',
          'Workflow & process automation',
          'Vector search & RAG pipelines',
          'Custom ML model deployment',
        ],
      },
      {
        id: 'blockchain',
        tag: '02 / intelligence',
        title: 'Blockchain Consulting',
        description:
          'Unlock the potential of your business with tailored blockchain solutions that drive automation, enhance transparency, and optimize decentralized operations.',
        features: [
          'Smart contract design & audit',
          'Decentralized app architecture',
          'Tokenization strategy',
          'Web3 integration consulting',
        ],
      },
      {
        id: 'strategy',
        tag: '03 / intelligence',
        title: 'IT Strategy & Consulting',
        description:
          'Transforming complex tech challenges into clear, actionable roadmaps tailored specifically to your business goals.',
        features: [
          'System architecture design',
          'Technology stack audits',
          'Scalability consulting',
          'Performance optimization assessments',
        ],
      },
    ],
  },
];

/* All services flattened, for the at-a-glance grid */
const ALL_SERVICES_GRID = [
  { tag: 'build', title: 'Full-Stack Development', desc: 'Custom web apps engineered for performance and scale.' },
  { tag: 'build', title: 'Web & App Development', desc: 'Fast, scalable web and mobile applications for growing businesses.' },
  { tag: 'build', title: 'Mobile Applications', desc: 'Modern iOS and Android solutions, cross-platform or native.' },
  { tag: 'build', title: 'Custom Software', desc: 'Business-focused solutions tailored to your workflows.' },
  { tag: 'cloud', title: 'DevOps & CI/CD', desc: 'Automated pipelines, continuous integration, seamless deployment.' },
  { tag: 'cloud', title: 'Cloud Deployment & Support', desc: 'Scalable cloud infrastructure with ongoing support.' },
  { tag: 'cloud', title: 'Cybersecurity Lab', desc: 'Enterprise-grade security, audits, and 24/7 threat monitoring.' },
  { tag: 'cloud', title: 'QA Testing & Automations', desc: 'Automated test suites and expert-led QA training.' },
  { tag: 'intelligence', title: 'AI Integrations & Automation', desc: 'Integrate AI capabilities into your existing applications.' },
  { tag: 'intelligence', title: 'Blockchain Consulting', desc: 'Tailored blockchain solutions for automation and transparency.' },
  { tag: 'intelligence', title: 'IT Strategy & Consulting', desc: 'Clear, actionable technology roadmaps for your goals.' },
  { tag: 'specialty', title: 'Election Technology', desc: 'Smart election management systems built for trust and scale.' },
  { tag: 'specialty', title: 'Technical Support', desc: 'Dedicated assistance with SLA-backed response times.' },
  { tag: 'specialty', title: 'Resource Augmentation', desc: 'Skilled professionals on demand, integrated with your team.' },
];

const STEPS = [
  { num: '01', title: 'Discover & Strategize', desc: 'We deep dive into your business needs, defining scope, architecture, and timelines.' },
  { num: '02', title: 'Design & Prototype', desc: 'Clean, minimalist, high-fidelity wireframes and user-centric flows.' },
  { num: '03', title: 'Iterative Development', desc: 'Clean code using modern frameworks with constant milestone updates.' },
  { num: '04', title: 'Continuous Deployment', desc: 'Secure staging, automated testing, and seamless production launch.' },
];

export default function Services() {
  const location = useLocation();

  /* Read ?category=...&service=... from the URL, or fall back to location.state */
  const params = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return {
      category: sp.get('category'),
      service: sp.get('service'),
    };
  }, [location.search]);

  /* Resolve initial category index */
  const initialCategoryIndex = useMemo(() => {
    const id = params.category || location.state?.category;
    if (!id) return 0;
    const idx = CATEGORIES.findIndex((c) => c.id === id);
    return idx >= 0 ? idx : 0;
  }, [params.category, location.state]);

  /* Resolve initial service index within that category */
  const initialServiceIndex = useMemo(() => {
    const id = params.service || location.state?.service;
    if (!id) return 0;
    const cat = CATEGORIES[initialCategoryIndex];
    const idx = cat.services.findIndex((s) => s.id === id);
    return idx >= 0 ? idx : 0;
  }, [params.service, location.state, initialCategoryIndex]);

  const [activeCategory, setActiveCategory] = useState(initialCategoryIndex);
  const [activeService, setActiveService] = useState(initialServiceIndex);
  const [clock, setClock] = useState(new Date());

  /* Re-sync state when the URL changes (e.g. user clicks another deep link) */
  useEffect(() => {
    setActiveCategory(initialCategoryIndex);
    setActiveService(initialServiceIndex);
  }, [initialCategoryIndex, initialServiceIndex]);

  const category = CATEGORIES[activeCategory];
  const service = category.services[activeService] || category.services[0];

  /* Live clock for the editorial rail */
  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  /* If the URL contains #core-services, scroll down to it once
     the tab/service state has been applied. */
  useEffect(() => {
    if (location.hash !== '#core-services') return;

    const id = requestAnimationFrame(() => {
      const el = document.getElementById('core-services');
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY - 24;
      window.scrollTo({ top, behavior: 'smooth' });
    });

    return () => cancelAnimationFrame(id);
  }, [location.hash, initialCategoryIndex, initialServiceIndex]);

  const handleCategoryChange = (idx) => {
    setActiveCategory(idx);
    setActiveService(0);
  };

  /* Grid card click — jump to the matching tab + service */
  const handleGridCardClick = (item) => {
    const cat = CATEGORIES.find((c) => c.id === item.tag);
    if (!cat) return;
    const svc = cat.services.find((s) => s.title === item.title);
    if (!svc) return;

    const cIdx = CATEGORIES.indexOf(cat);
    const sIdx = cat.services.indexOf(svc);

    setActiveCategory(cIdx);
    setActiveService(sIdx);

    window.history.replaceState(
      null,
      '',
      `/services?category=${cat.id}&service=${svc.id}#core-services`
    );

    const el = document.getElementById('core-services');
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 24;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <div className="services-page">
      <div className="services-page__grain" aria-hidden="true" />

      {/* Editorial side rails */}
      <div className="services-page__rail services-page__rail--left">
        <span className="services-page__rail-text">AVTECH · SERVICES</span>
        <span className="services-page__rail-line" />
        <span className="services-page__rail-text">
          {clock.toLocaleTimeString('en-US', { hour12: false })}
        </span>
      </div>
      <div className="services-page__rail services-page__rail--right">
        <span className="services-page__rail-text">BARAMATI · IN</span>
        <span className="services-page__rail-line" />
        <span className="services-page__rail-text">↓</span>
      </div>

      {/* Giant background typography */}
      <div className="services-page__giant-type" aria-hidden="true">
        <span>CAPABILITIES</span>
      </div>

      {/* ============ HERO ============ */}
      <header className="services-page__hero">
        <div className="services-page__hero-inner">
          <div className="services-page__hero-badge">
            <span className="services-page__hero-badge-dot" />
            <span className="services-page__tag">Our Capabilities</span>
          </div>

          <h1 className="services-page__hero-title">
            Engineered solutions,
            <br />
            <em>scaled to perfection.</em>
          </h1>

          <p className="services-page__hero-sub">
            From development and cloud hosting to AI integrations, cybersecurity, and
            hands-on workshops — we build digital ecosystems and upskill teams for
            measurable success.
          </p>

          <div className="services-page__hero-meta-row">
            <div className="services-page__hero-meta-item">
              <span className="services-page__meta-label">Services</span>
              <span className="services-page__meta-value">14+</span>
            </div>
            <div className="services-page__hero-meta-item">
              <span className="services-page__meta-label">Status</span>
              <span className="services-page__meta-value services-page__meta-value--live">
                <span className="services-page__live-dot" /> Available
              </span>
            </div>
            <div className="services-page__hero-meta-item">
              <span className="services-page__meta-label">Practice Areas</span>
              <span className="services-page__meta-value">03</span>
            </div>
            <div className="services-page__hero-meta-item">
              <span className="services-page__meta-label">Support</span>
              <span className="services-page__meta-value">24/7</span>
            </div>
          </div>
        </div>
      </header>

      {/* ============ INTERACTIVE TABS ============ */}
      <section id="core-services" className="services-page__main">
        <div className="services-page__container">
          <div className="services-page__section-head">
            <h2 className="services-page__section-title">Core Services</h2>
            <span className="services-page__section-num">01 / Capabilities</span>
          </div>

          {/* Category tabs */}
          <div className="services-page__category-nav">
            {CATEGORIES.map((cat, idx) => (
              <button
                key={cat.id}
                className={`services-page__category-btn ${
                  activeCategory === idx ? 'services-page__category-btn--active' : ''
                }`}
                onClick={() => handleCategoryChange(idx)}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Service pills for active category */}
          <div className="services-page__service-nav">
            {category.services.map((svc, idx) => (
              <button
                key={svc.id}
                className={`services-page__service-btn ${
                  activeService === idx ? 'services-page__service-btn--active' : ''
                }`}
                onClick={() => setActiveService(idx)}
              >
                <span className="services-page__service-btn-index">
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <span>{svc.title}</span>
              </button>
            ))}
          </div>

          {/* Active service detail */}
          <div className="services-page__detail">
            <div className="services-page__detail-meta">
              <span className="services-page__detail-tag">{service.tag}</span>
              <h3 className="services-page__detail-title">{service.title}</h3>
              <p className="services-page__detail-desc">{service.description}</p>
            </div>
            <div className="services-page__detail-body">
              <h4 className="services-page__detail-body-title">Key Capabilities</h4>
              <ul className="services-page__detail-features">
                {service.features.map((feature, idx) => (
                  <li key={idx} className="services-page__detail-feature">
                    <span className="services-page__detail-feature-check">✓</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============ AT A GLANCE GRID ============ */}
      <section className="services-page__grid-section">
        <div className="services-page__container">
          <div className="services-page__section-head">
            <h2 className="services-page__section-title">All Services at a Glance</h2>
            <span className="services-page__section-num">02 / Overview</span>
          </div>

          <div className="services-page__grid">
            {ALL_SERVICES_GRID.map((item, idx) => (
              <button
                key={idx}
                type="button"
                className="services-page__grid-card"
                onClick={() => handleGridCardClick(item)}
              >
                <span className="services-page__grid-card-tag">{item.tag}</span>
                <h3 className="services-page__grid-card-title">{item.title}</h3>
                <p className="services-page__grid-card-desc">{item.desc}</p>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PROCESS ============ */}
      <section className="services-page__process">
        <div className="services-page__container">
          <div className="services-page__process-header">
            <span className="services-page__eyebrow">How we work</span>
            <h2 className="services-page__process-title">
              A proven, seamless engineering pipeline.
            </h2>
          </div>

          <div className="services-page__process-grid">
            {STEPS.map((step) => (
              <div key={step.num} className="services-page__process-step">
                <span className="services-page__step-num">{step.num}</span>
                <h4 className="services-page__step-title">{step.title}</h4>
                <p className="services-page__step-desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section className="services-page__cta">
        <div className="services-page__cta-inner">
          <span className="services-page__cta-tag">Let's build</span>
          <h2 className="services-page__cta-title">
            Have a project or workshop in mind?
          </h2>
          <p className="services-page__cta-sub">
            Let's collaborate to build reliable software or upskill your team with
            modern tech workshops.
          </p>
          <a href="/contact" className="services-page__cta-btn">
            <span>Start a conversation</span>
            <span className="services-page__cta-arrow">→</span>
          </a>
        </div>
      </section>
    </div>
  );
}